import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { signOut } from "firebase/auth";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { AuthContext } from "../context/AuthContext";
import OrganizationSettings from "./OrganizationSettings";

import {
  Users,
  Briefcase,
  LayoutDashboard,
  ClipboardList,
  LogOut,
  Activity,
  ChevronRight,
  ShieldCheck,
  Settings
} from "lucide-react";

import EmployeesSection from "./EmployeesSection";
import ClientsSection from "./ClientsSection";
import AssignProject from "./AssignProject";
import AdminTimeAnalytics from "./AdminTimeAnalytics";

function AdminDashboard() {

  const navigate = useNavigate();
  const { orgSlug: urlSlug } = useParams();

  const [activePage, setActivePage] = useState("dashboard");

  const [employeeCount, setEmployeeCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [assignmentCount, setAssignmentCount] = useState(0);
  const [orgName, setOrgName] = useState("");
  const { orgId, orgSlug, email , role } = useContext(AuthContext);

  /* Redirect protection */
  useEffect(() => {
    if (orgSlug && urlSlug !== orgSlug) {
      navigate(`/${orgSlug}/admin-dashboard`);
    }
  }, [urlSlug, orgSlug]);

  /* Fetch Organization Name */
 /* Fetch Organization Name */
useEffect(() => {

  const fetchOrg = async () => {

    if (!orgId) return;

    const ref = doc(db, "organizations", orgId);
    const snap = await getDoc(ref);

    if (snap.exists()) {
      const data = snap.data();
      setOrgName(data.basic?.companyName || "Organization");
    }

  };

  fetchOrg();

}, [orgId]);
const isDev = process.env.NODE_ENV === "development";
if (isDev) console.log("CURRENT ROLE:", role);

  /* Real-time listeners */
  useEffect(() => {

    if (!orgId) return;

    const unsubEmp = onSnapshot(
      query(collection(db, "organizations", orgId, "users"), where("role", "==", "employee")),
      (snap) => setEmployeeCount(snap.size)
    );

    const unsubProj = onSnapshot(
      collection(db, "organizations", orgId, "projects"),
      (snap) => setProjectCount(snap.size)
    );

    const unsubAssign = onSnapshot(
  query(
    collection(db, "organizations", orgId, "projects"),
    where("status", "==", "assigned")   // only assigned projects
  ),
  (snap) => setAssignmentCount(snap.size)
);

    return () => {
      unsubEmp();
      unsubProj();
      unsubAssign();
    };

  }, [orgId]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">

      {/* SIDEBAR */}
      <aside className="w-72 bg-white border-r border-slate-200 hidden md:flex flex-col sticky top-0 h-screen">

        {/* Logo */}
        <div className="flex items-center px-5 py-4">
          <img
            src="/logo.png"
            alt="Avaami Pulse"
            className="w-12 h-12 object-contain mr-2"
          />
          <h1 className="text-xl font-bold text-slate-900">
            Avaami <span className="text-[#1A73E8]">Pulse</span>
          </h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-5 mt-6 space-y-2">

          <SidebarLink
            active={activePage === "dashboard"}
            onClick={() => setActivePage("dashboard")}
            icon={<LayoutDashboard size={18} />}
            label="Overview"
          />

          <SidebarLink
            active={activePage === "employees"}
            onClick={() => setActivePage("employees")}
            icon={<Users size={18} />}
            label="Employees"
          />

          <SidebarLink
            active={activePage === "clients"}
            onClick={() => setActivePage("clients")}
            icon={<Briefcase size={18} />}
            label="Clients"
          />

          <SidebarLink
            active={activePage === "assign"}
            onClick={() => setActivePage("assign")}
            icon={<ClipboardList size={18} />}
            label="Project Assignments"
          />

          <SidebarLink
            active={activePage === "analytics"}
            onClick={() => setActivePage("analytics")}
            icon={<Activity size={18} />}
            label="Time Analytics"
          />

          <SidebarLink
            active={activePage === "settings"}
            onClick={() => setActivePage("settings")}
            icon={<Settings size={18} />}
            label="Organization Settings"
          />
         {email === "support@avaami.com" && (
  <SidebarLink
    onClick={() => navigate("/super-admin")}
    icon={<ShieldCheck size={18} />}
    label="Super Admin Panel"
  />
)}
 </nav>

        {/* Logout */}
        <div className="p-6 border-t">

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-red-500 text-white py-3 rounded-xl font-semibold transition"
          >
            <LogOut size={16} />
            Sign Out
          </button>

        </div>

      </aside>

      {/* MAIN */}
      <div className="flex-1 flex flex-col">

        {/* HEADER */}
        <header className="bg-white border-b px-10 py-5 flex justify-between items-center">

          <div>

            <p className="text-xs text-slate-400 uppercase font-semibold">
              Organization
            </p>

            <h1 className="text-xl font-bold text-slate-900">
              {orgName || "Loading organization..."}
            </h1>

          </div>

          <div
  onClick={() => setActivePage("settings")}
  className="h-10 w-10 rounded-xl bg-[#1A73E8] text-white flex items-center justify-center font-bold cursor-pointer hover:scale-105 transition"
>
  {orgName?.charAt(0)}
</div>

        </header>

        {/* CONTENT */}
        <main className="p-10 max-w-7xl mx-auto w-full">

          {activePage === "dashboard" && (

            <div className="space-y-10">

              {/* Stats */}
              <div className="grid md:grid-cols-3 gap-6">

                <StatCard
                  label="Total Employees"
                  count={employeeCount}
                  icon={<Users />}
                />

                <StatCard
                  label="Active Projects"
                  count={projectCount}
                  icon={<Briefcase />}
                />

                <StatCard
                  label="Current Assignments"
                  count={assignmentCount}
                  icon={<ClipboardList />}
                />

              </div>

              {/* Infrastructure Card */}
              <div className="grid lg:grid-cols-2 gap-6">

                <div className="bg-white p-8 rounded-2xl border">

                  <div className="flex items-center justify-between mb-6">

                    <h3 className="text-lg font-semibold text-slate-900">
                      Platform Status
                    </h3>

                    <ShieldCheck className="text-green-500" />

                  </div>

                  <div className="space-y-4">

                    <StatusItem
                      label="Secure Data Isolation"
                      status="Active"
                    />

                    <StatusItem
                      label="Cloud Infrastructure"
                      status="Operational"
                    />

                    <StatusItem
                      label="Realtime Synchronization"
                      status="Running"
                    />

                  </div>

                </div>

                <div className="bg-[#1A73E8] text-white p-8 rounded-2xl">

                  <h3 className="text-xl font-semibold mb-3">
                    Organization Overview
                  </h3>

                  <p className="text-blue-100 leading-relaxed">

                    Avaami Pulse helps your organization manage employees,
                    clients, and projects from a unified dashboard. Monitor
                    productivity, assign work efficiently, and analyze time
                    usage across your teams.

                  </p>

                  <button
                    onClick={() => setActivePage("employees")}
                    className="mt-6 bg-white text-[#1A73E8] px-6 py-2 rounded-lg font-semibold"
                  >
                    View Employees
                  </button>

                </div>

              </div>

            </div>

          )}

          {activePage === "employees" && <EmployeesSection orgId={orgId} />}

          {activePage === "clients" && <ClientsSection orgId={orgId} />}

          {activePage === "assign" && <AssignProject orgId={orgId} />}

          {activePage === "analytics" && <AdminTimeAnalytics orgId={orgId} />}

          {activePage === "settings" && <OrganizationSettings />}

        </main>

      </div>

    </div>
  );
}

/* Sidebar Component */

const SidebarLink = ({ active, onClick, icon, label }) => (

  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition
      ${active
        ? "bg-[#1A73E8] text-white"
        : "text-slate-600 hover:bg-slate-100"
      }`}
  >

    <div className="flex items-center gap-3">
      {icon}
      {label}
    </div>

    {active && <ChevronRight size={16} />}

  </button>

);

/* Stats Card */

const StatCard = ({ label, count, icon }) => (

  <div className="bg-white p-8 rounded-2xl border">

    <div className="w-12 h-12 bg-blue-50 text-[#1A73E8] rounded-lg flex items-center justify-center mb-4">
      {icon}
    </div>

    <p className="text-sm text-slate-500 mb-1">{label}</p>

    <p className="text-3xl font-bold text-slate-900">{count}</p>

  </div>

);

/* Status */

const StatusItem = ({ label, status }) => (

  <div className="flex justify-between text-sm">

    <span className="text-slate-600">{label}</span>

    <span className="text-green-600 font-semibold">{status}</span>

  </div>

);

export default AdminDashboard;